/**
 * Lönerapport — admin-vy för lönegrundande tid per period.
 * Route: /lonerapport (adminOnly)
 *
 * - Filter: period (from/to), anställd, arbetsplats
 * - Tabell: per anställd med brutto/avdrag/netto + expanderbara dagar
 * - Export: CSV, PAXml 2.2 (Fortnox/Visma/Hogia/Kontek), PDF
 */
import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Receipt,
  Download,
  FileCsv,
  FilePdf,
  FileCode,
  CaretDown,
  CaretRight,
  Users,
  Calendar,
  MapPin,
  Coffee,
  PencilLine,
  Lock,
  Warning,
  Sparkle,
} from "@phosphor-icons/react";

function fmtHHMM(minutes) {
  const m = Number(minutes) || 0;
  if (m < 0) return "0h 00min";
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${String(rest).padStart(2, "0")}min`;
}

function fmtHours(minutes) {
  const m = Number(minutes) || 0;
  return (m / 60).toFixed(2);
}

function firstDayOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

// Lokal svensk tid (Europe/Stockholm) — utan detta visas UTC-timmen.
function formatLocalTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

function buildQuery({ dateFrom, dateTo, userId, workplaceId }) {
  const q = new URLSearchParams();
  q.set("date_from", dateFrom);
  q.set("date_to", dateTo);
  if (userId) q.set("user_id", userId);
  if (workplaceId) q.set("workplace_id", workplaceId);
  return q.toString();
}

export default function Lonerapport() {
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [userId, setUserId] = useState("");
  const [workplaceId, setWorkplaceId] = useState("");
  const [users, setUsers] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // user_id -> bool
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [u, w] = await Promise.all([
          api.get("/users"),
          api.get("/workday/workplaces"),
        ]);
        setUsers((u.data || []).filter((x) => x.active !== false));
        setWorkplaces((w.data || []).filter((x) => x.active !== false));
      } catch (e) {
        console.error(e);
      }
    };
    loadFilters();
  }, []);

  const runReport = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Ange både startdatum och slutdatum");
      return;
    }
    setLoading(true);
    try {
      const qs = buildQuery({ dateFrom, dateTo, userId, workplaceId });
      const r = await api.get(`/payroll/report?${qs}`);
      setReport(r.data);
      // Expandera automatiskt om bara 1 anställd
      if (r.data.employees?.length === 1) {
        setExpanded({ [r.data.employees[0].user_id]: true });
      }
    } catch (e) {
      // Rensa stale-data så användaren inte tror gamla siffrorna gäller nya perioden
      setReport(null);
      setExpanded({});
      toast.error(e.response?.data?.detail || "Kunde inte hämta rapport");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Kör initial rapport när sidan öppnas
    runReport();
  }, []);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const qs = buildQuery({ dateFrom, dateTo, userId, workplaceId });
      // Ladda blob med credentials så cookies följer med
      const res = await fetch(`${API}/payroll/export/${format}?${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext = format === "paxml" ? "xml" : format;
      const filename = `lonerapport-${dateFrom}-${dateTo}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} nedladdad`);
    } catch (e) {
      toast.error(`Kunde inte exportera ${format.toUpperCase()}: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  const totals = report?.grand_totals;

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl">Lönerapport</h1>
          <p className="text-sm text-text-muted mt-1">
            Aggregerad lönegrundande arbetstid per period. Exportera direkt till
            ditt lönesystem.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => doExport("csv")}
            disabled={exporting || !report || !totals?.workday_count}
            className="tk-btn-secondary flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <FileCsv size={16} weight="duotone" />
            {exporting === "csv" ? "Exporterar..." : "CSV"}
          </button>
          <button
            onClick={() => doExport("paxml")}
            disabled={exporting || !report || !totals?.workday_count}
            className="tk-btn-secondary flex items-center gap-2"
            data-testid="export-paxml-btn"
            title="PAXml 2.2 — importeras direkt i Fortnox, Visma Lön, Hogia och Kontek"
          >
            <FileCode size={16} weight="duotone" />
            {exporting === "paxml" ? "Exporterar..." : "PAXml"}
          </button>
          <button
            onClick={() => doExport("pdf")}
            disabled={exporting || !report || !totals?.workday_count}
            className="tk-btn-primary flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            <FilePdf size={16} weight="duotone" />
            {exporting === "pdf" ? "Exporterar..." : "PDF"}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div
        className="bg-white border border-app rounded-2xl p-5 shadow-sm"
        data-testid="payroll-filters"
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Från
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="payroll-date-from"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Till
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="payroll-date-to"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Anställd
            </label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="payroll-user-filter"
            >
              <option value="">Alla anställda</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Arbetsplats
            </label>
            <select
              value={workplaceId}
              onChange={(e) => setWorkplaceId(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="payroll-workplace-filter"
            >
              <option value="">Alla arbetsplatser</option>
              {workplaces.map((w) => (
                <option key={w.workplace_id} value={w.workplace_id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={runReport}
            disabled={loading}
            className="tk-btn-primary h-[38px]"
            data-testid="payroll-run-btn"
          >
            {loading ? "Hämtar..." : "Uppdatera rapport"}
          </button>
        </div>
      </div>

      {/* Grand summary */}
      {totals && (
        <>
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="payroll-grand-totals"
          >
            <SummaryTile
              icon={<Users size={20} weight="duotone" />}
              label="Anställda"
              value={totals.employee_count}
            />
            <SummaryTile
              icon={<Calendar size={20} weight="duotone" />}
              label="Arbetsdagar"
              value={totals.workday_count}
              sub={
                totals.approved_count != null
                  ? `${totals.approved_count} godkända · ${totals.unapproved_count} kvar`
                  : null
              }
            />
            <SummaryTile
              icon={<Receipt size={20} weight="duotone" />}
              label="Brutto"
              value={fmtHHMM(totals.gross_minutes)}
              sub={`Avdrag rast: ${fmtHHMM(totals.deducted_minutes)}`}
            />
            <SummaryTile
              icon={<Receipt size={20} weight="fill" />}
              label="Lönegrundande netto"
              value={fmtHHMM(totals.net_minutes)}
              sub={`${fmtHours(totals.net_minutes)} timmar`}
              highlight
            />
          </div>
          {totals.unapproved_count > 0 && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-start gap-3"
              data-testid="payroll-unapproved-warning"
            >
              <Warning
                size={18}
                weight="fill"
                className="text-amber-700 shrink-0 mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-amber-900">
                  {totals.unapproved_count} arbetsdag
                  {totals.unapproved_count === 1 ? "" : "ar"} är ej godkända
                </div>
                <div className="text-amber-800 mt-0.5">
                  Exporten inkluderar alla dagar oavsett attest-status. Gå till{" "}
                  <a
                    href="/attest"
                    className="underline hover:text-amber-900 font-medium"
                  >
                    Attest
                  </a>{" "}
                  för att godkänna innan du skickar underlaget till lönesystemet.
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {report && (report.employees?.length ?? 0) === 0 && (
        <div
          className="bg-white border border-app rounded-2xl p-10 text-center"
          data-testid="payroll-empty"
        >
          <div className="w-14 h-14 rounded-full bg-brand/10 text-brand flex items-center justify-center mx-auto mb-4">
            <Receipt size={28} weight="duotone" />
          </div>
          <h3 className="font-heading text-lg mb-1">Inga arbetsdagar i perioden</h3>
          <p className="text-sm text-text-muted">
            Ändra filter eller be dina anställda stämpla in.
          </p>
        </div>
      )}

      {/* Per-employee list */}
      {report?.employees?.length > 0 && (
        <div className="space-y-3">
          {report.employees.map((emp) => (
            <EmployeeCard
              key={emp.user_id}
              emp={emp}
              expanded={!!expanded[emp.user_id]}
              onToggle={() =>
                setExpanded((prev) => ({
                  ...prev,
                  [emp.user_id]: !prev[emp.user_id],
                }))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ icon, label, value, sub, highlight }) {
  return (
    <div
      className={`rounded-xl p-4 border ${
        highlight
          ? "bg-brand/5 border-brand/30"
          : "bg-white border-app"
      }`}
    >
      <div className="flex items-center gap-2 text-text-muted text-xs uppercase tracking-widest">
        {icon}
        {label}
      </div>
      <div
        className={`font-heading text-2xl mt-2 ${
          highlight ? "text-brand" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function EmployeeCard({ emp, expanded, onToggle }) {
  const t = emp.totals;
  return (
    <div
      className="bg-white border border-app rounded-2xl shadow-sm overflow-hidden"
      data-testid={`payroll-emp-${emp.user_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-app text-left"
        data-testid={`payroll-emp-toggle-${emp.user_id}`}
      >
        <div className="shrink-0 text-text-muted">
          {expanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-base">{emp.user_name}</div>
          <div className="text-xs text-text-muted mt-0.5 flex items-center gap-3 flex-wrap">
            {emp.employment_number ? (
              <span>Anst.nr {emp.employment_number}</span>
            ) : (
              <span className="text-amber-700">
                ⚠ Saknar anst.nr (sätts under Anställda)
              </span>
            )}
            <span>{t.workday_count} arbetsdagar</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-right shrink-0">
          <div>
            <div className="text-[10px] text-text-muted uppercase tracking-wider">
              Brutto
            </div>
            <div className="font-heading text-base tabular-nums">
              {fmtHHMM(t.gross_minutes)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-text-muted uppercase tracking-wider">
              Rast
            </div>
            <div className="font-heading text-base tabular-nums">
              {fmtHHMM(t.deducted_minutes)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-brand uppercase tracking-wider font-medium">
              Netto
            </div>
            <div className="font-heading text-base text-brand tabular-nums">
              {fmtHHMM(t.net_minutes)}
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-app px-5 py-4 bg-app/30">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid={`payroll-emp-days-${emp.user_id}`}
            >
              <thead className="text-text-muted text-xs uppercase tracking-wider">
                <tr className="border-b border-app">
                  <th className="text-left py-2 pr-3">Datum</th>
                  <th className="text-left py-2 pr-3">Arbetsplats</th>
                  <th className="text-left py-2 pr-3">In</th>
                  <th className="text-left py-2 pr-3">Ut</th>
                  <th className="text-right py-2 pr-3">Brutto</th>
                  <th className="text-right py-2 pr-3">Rast</th>
                  <th className="text-right py-2 pr-3">Netto</th>
                  <th className="text-center py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {emp.workdays.map((wd) => (
                  <tr
                    key={wd.workday_id}
                    className="border-b border-app/50 last:border-0"
                  >
                    <td className="py-2 pr-3 tabular-nums">
                      {(wd.stamp_in || "").slice(0, 10)}
                    </td>
                    <td className="py-2 pr-3">
                      {wd.workplace_name ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} className="text-text-muted" />
                          {wd.workplace_name}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      {formatLocalTime(wd.stamp_in)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      {formatLocalTime(wd.stamp_out)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmtHHMM(wd.gross_minutes)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {wd.deducted_breaks.length > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 text-text-muted"
                          title={wd.deducted_breaks
                            .map(
                              (b) =>
                                `${b.name} (${b.start_time}–${b.end_time}, ${b.duration_minutes} min)`
                            )
                            .join("\n")}
                        >
                          <Coffee size={12} />
                          {fmtHHMM(wd.deducted_minutes)}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">
                      {fmtHHMM(wd.net_minutes)}
                    </td>
                    <td className="py-2 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        {wd.approved_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-emerald-700"
                            title={`Godkänd av ${wd.approved_by_name || "?"} · ${(wd.approved_at || "").slice(0, 16)}`}
                          >
                            <Lock size={11} weight="fill" />
                          </span>
                        ) : wd.employee_marked_done_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-blue-700"
                            title="Klar för attest"
                          >
                            <Sparkle size={11} weight="fill" />
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-amber-600"
                            title="Väntar på attest"
                          >
                            <Warning size={11} />
                          </span>
                        )}
                        {wd.edited_at && (
                          <PencilLine
                            size={11}
                            className="text-amber-600"
                            title={`Rättad ${wd.edited_at.slice(0, 16)}`}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="font-medium">
                <tr className="border-t-2 border-app">
                  <td className="py-2 pr-3" colSpan={4}>
                    Summa {emp.user_name}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtHHMM(t.gross_minutes)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtHHMM(t.deducted_minutes)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-brand">
                    {fmtHHMM(t.net_minutes)}
                  </td>
                  <td className="py-2 text-center text-xs text-text-muted">
                    {fmtHours(t.net_minutes)} h
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
